import React, { useEffect, useState, useCallback } from "react";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import moment from 'moment';
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllLoanOxyzo,
  fetchFilteredAllLoanOxyzo,
  uploadAllLoanFile
} from "../../../redux/slice/allLoanOxyzoReport";
import { downloadOxyzoReport } from "../../../redux/slice/collectionOxyzoReport";
import { HashLoader } from "react-spinners";
import { Card, Button, Input, Select, Space, Table, Alert, Empty, DatePicker, Upload, message, Radio, Tooltip } from "antd";
import { StyledTable, StyledButton } from "../components/styleComp";
import { useAuth } from "../../../context/AuthContext";
import jwt_decode from "jwt-decode";
import { UploadOutlined, FileExcelOutlined, InfoCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../redux/apiConfig";

const { Option } = Select;

const AllLoanReportOxyzo = (props) => {
  const [fileList, setFileList] = useState([]);
  const [uploadType, setUploadType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const [buttonLoading, setButtonLoading] = useState(false);

  const { authState } = useAuth();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    pages: { allLoanOxyzoData: { page: 1, pageSize: 7 } },
    filters: { allLoanOxyzoData: {} },
    searchInputs: { allLoanOxyzoData: "" },
    selectedColumns: { allLoanOxyzoData: "" },
    hasSearched: { allLoanOxyzoData: false },
  });

  const [triggerFilter, setTriggerFilter] = useState(null);

  const jwtBearer = authState?.token;
  const tokenDecoded = jwtBearer ? jwt_decode(jwtBearer) : null;
  const jwtPayload = tokenDecoded
    ? {
      group: props?.userDetails?.group_id,
      user: tokenDecoded?.user_id,
      role: props?.userDetails?.role,
      expiry: tokenDecoded?.exp,
    }
    : {};

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state?.pages?.allLoanOxyzoData;
      dispatch(
        fetchAllLoanOxyzo({
          jwtBearer,
          page,
          filters: state?.filters?.allLoanOxyzoData,
          pageSize,
        })
      );
    }
  }, [jwtBearer, state?.pages?.allLoanOxyzoData, state?.filters?.allLoanOxyzoData, dispatch]);

  const {
    data: allLoanOxyzoData = [],
    filteredData: filteredAllLoanOxyzoData = [],
    totalCount: totalAllLoanOxyzoCount,
    loading: isLoadingAllLoanOxyzo,
    filteredLoading: isLoadingFilteredAllLoanOxyzo,
    error: errorAllLoanOxyzo,
  } = useSelector((state) => state?.oxyzoAllLoan || {});

  const defineColumns = useCallback((data) => {
    if (!data || data?.length === 0) return [];
    const keys = Object.keys(data[0]);
    return keys.map((key) => ({
      title: <strong style={{ fontSize: "18px" }}>{key.replace(/_/g, " ").toUpperCase()}</strong>,
      dataIndex: key,
      key: key,
      render: (text) => <span style={{ fontSize: "16px" }}>{text}</span>,
    }));
  }, []);

  const handleKeyDown = (event, type) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleFilter(type);
    }
  };

  const handleFilter = (type) => {
    trackFeatureAction("Reports", "All Loan Report", "search");
    const columnName = state?.selectedColumns?.[type]?.toLowerCase();
    const trimmedInput = state?.searchInputs?.[type]?.trim();

    dispatch(
      fetchFilteredAllLoanOxyzo({
        jwtBearer,
        filters: { [columnName]: trimmedInput },
      })
    );

    setState((prevState) => ({
      ...prevState,
      hasSearched: {
        ...prevState?.hasSearched,
        [type]: true,
      },
      pages: {
        ...prevState?.pages,
        [type]: { ...prevState?.pages?.[type], page: 1 },
      },
    }));
  };

  const handleReset = (type) => {
    trackFeatureAction("Reports", "All Loan Report", "reset");
    setState((prevState) => ({
      ...prevState,
      searchInputs: {
        ...prevState?.searchInputs,
        [type]: "",
      },
      selectedColumns: {
        ...prevState?.selectedColumns,
        [type]: "",
      },
      hasSearched: {
        ...prevState?.hasSearched,
        [type]: false,
      },
      filters: {
        ...prevState?.filters,
        [type]: {},
      },
    }));
    setTriggerFilter(type);
  };

  const paginationConfig = (type) => {
    const total = state?.hasSearched?.allLoanOxyzoData
      ? filteredAllLoanOxyzoData?.length
      : totalAllLoanOxyzoCount;

    return {
      current: state?.pages?.allLoanOxyzoData?.page,
      pageSize: state?.pages?.allLoanOxyzoData?.pageSize,
      total: total,
      showTotal: (total, [start, end]) => `${start}-${end} of ${total} items`,
      onChange: (page, pageSize) =>
        setState((prevState) => ({
          ...prevState,
          pages: { ...prevState?.pages, [type]: { page, pageSize } },
        })),
    };
  };

  const renderSearchBar = (columns, type) => (
    <Space style={{ marginBottom: 16, marginLeft: 2 }}>
      <Select
        placeholder="Select Column"
        style={{ width: 200, height: 32, lineHeight: "32px" }}
        value={state?.selectedColumns?.[type] || undefined}
        onChange={(value) =>
          setState((prevState) => ({
            ...prevState,
            selectedColumns: {
              ...prevState?.selectedColumns,
              [type]: value,
            },
          }))
        }
      >
        {columns?.map((col) => (
          <Option key={col?.key} value={col?.dataIndex}>
            {col?.title?.props?.children}
          </Option>
        ))}
      </Select>
      <Input
        placeholder="Value"
        value={state?.searchInputs?.[type]}
        onChange={(e) =>
          setState((prevState) => ({
            ...prevState,
            searchInputs: {
              ...prevState?.searchInputs,
              [type]: e.target.value,
            },
          }))
        }
        onKeyDown={(e) => handleKeyDown(e, type)}
        style={{ width: 150, height: 32, lineHeight: "32px" }}
        disabled={!state?.selectedColumns?.[type]}
      />
      <Button
        type="primary"
        onClick={() => handleFilter(type)}
        style={{ height: 32, lineHeight: "32px" }}
      >
        Search
      </Button>
      {state?.hasSearched?.allLoanOxyzoData && (
        <Button
          onClick={() => handleReset(type)}
          style={{ width: 70, height: 32, lineHeight: "32px" }}
        >
          Reset
        </Button>
      )}
    </Space>
  );

// Call API on button click
const handleAPIDownload = () => {
  trackFeatureAction("Reports", "All Loan Report", "download");
  console.log("in handleAPIDownload");
  setButtonLoading(true);
  dispatch(downloadOxyzoReport({ jwtBearer, startDate: "", endDate: "", type: "all_loan" }))
    .then(() => setButtonLoading(false))
    .catch(() => setButtonLoading(false));
};


  const handleFileChange = ({ file }) => {
    if (file.status === "removed") {
      setFileList([]);
      return;
    }

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      message.error("Invalid file format. Allowed format: .xlsx");
      setStatusMessage({ type: "error", text: "Only .xlsx files are allowed." });
      return;
    }

    setFileList([file]);
    setStatusMessage(null);
  };

  
  const handleUpload = async () => {
    trackFeatureAction("Reports", "All Loan Report", "upload");
    if (!fileList.length) {
      message.error("Please select a file to upload!");
      setStatusMessage({ type: "error", text: "No file selected. Please choose a file." });
      return;
    }
  
    if (!uploadType) {
      message.error("Please select an upload type!");
      setStatusMessage({ type: "error", text: "No upload type selected. Please choose one." });
      return;
    }
  
    setLoading(true);
    setStatusMessage(null);
  
    const file = fileList[0];
    const formData = new FormData();
    formData.append("file", file.originFileObj);
  
    try {
      const response = await dispatch(uploadAllLoanFile({ file, uploadType })).unwrap();

      console.log("response",response)
      
      message.success(response?.message || "File uploaded successfully!");
      setStatusMessage({ type: "success", text: response?.message || "Upload successful!" });
  
      // Reset file input
      setFileList([]);
      setUploadType(null);
  
      // Fetch updated data
      dispatch(fetchAllLoanOxyzo({ jwtBearer }));
  
    } catch (error) {
      console.log("Error---",error);
      setStatusMessage({ type: "error", text: "An error occurred. Please contact the support team." });

    } finally {
      setLoading(false);
    }
  };
  

  return (
    <>
      {/* <Card
        style={{
          width: "60%",
          margin: "20px auto",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          borderRadius: "5px",
          padding: "20px",
        }}
        bordered
      >
        <center>
          <p>
            <strong>
              <FileExcelOutlined /> UPLOAD ALL LOAN REPORT
            </strong>
          </p>

          <div style={{ display: "flex", justifyContent: "center" }}>
          <Upload
            customRequest={({ onSuccess }) => onSuccess("ok")}
            name="file"
            fileList={fileList}
            listType="picture-card"
            onChange={handleFileChange}
            maxCount={1}
          >
            {fileList.length === 0 && (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Select File</div>
              </div>
            )}
          </Upload>
          </div>

          <div>
            <p>
              <strong>Select Upload Type</strong>
              <Tooltip
                title={
                  <span>
                    <strong>Fresh</strong> - Overwrites old data.
                    <br />
                    <strong>Append</strong> - Adds new data without deleting old data.
                  </span>
                }
              >
                <InfoCircleOutlined
                  style={{
                    marginLeft: "5px",
                    cursor: "pointer",
                    color: "dodgerblue",
                    fontSize: "16px",
                  }}
                />
              </Tooltip>
            </p>
            <Radio.Group onChange={(e) => setUploadType(e.target.value)} value={uploadType}>
              <Radio value="Fresh">Fresh</Radio>
              <Radio value="Append">Append</Radio>
            </Radio.Group>
          </div>

          <Button
            style={{
              backgroundColor: "#1890ff",
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              fontWeight: "bold",
              marginTop: "20px",
            }}
            onClick={handleUpload}
            loading={loading}
            disabled={fileList.length === 0 || !uploadType}
          >
            Upload
          </Button>

          {statusMessage && (
  <div style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "20px"
  }}>
    <Alert
      style={{
        width: "50%",
        borderRadius: "8px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        animation: "fadeIn 0.5s ease-in-out"
      }}
      message={statusMessage.type === "success" ? "🎉 Success" : "⚠️ Error"}
      description={statusMessage.text}
      type={statusMessage.type}
    />
  </div>
)}



          <div style={{ textAlign: "justify", marginTop: "20px" }}>
            <p><strong>Instructions to be followed:</strong></p>
            <p>1. The file format should be <strong>.xlsx</strong>.</p>
            <p>2. Do not change any headers of the report you downloaded from Oxyzo's Portal.</p>
          </div>
        </center>
      </Card> */}
      <Card
        title={<span style={{ fontWeight: 600, fontSize: "24px", fontFamily: "poppins-font, sans-serif" }}>ALL LOAN REPORT</span>}
        bordered
        extra={
          <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <StyledButton
                type="primary"
                onClick={handleAPIDownload}
                loading={buttonLoading}
              >
                Download Report
              </StyledButton>
            </div>
          </div>
        }
      >
        {renderSearchBar(defineColumns(allLoanOxyzoData || []), "allLoanOxyzoData")}

        {(isLoadingFilteredAllLoanOxyzo || isLoadingAllLoanOxyzo) ? (
          <div className="spinner-container">
            <HashLoader color="#f57171" loading={true} />
          </div>
        ) : errorAllLoanOxyzo ? (
          <Alert message={errorAllLoanOxyzo} type="error" />
        ) : (
          <StyledTable
            columns={defineColumns(allLoanOxyzoData)}
            dataSource={
              state?.hasSearched?.allLoanOxyzoData
                ? filteredAllLoanOxyzoData
                : allLoanOxyzoData
            }
            pagination={paginationConfig("allLoanOxyzoData")}
            locale={{ emptyText: <Empty description="No data available" /> }}
          />
        )}
      </Card>
    </>
  );
};

export default AllLoanReportOxyzo;